import { formatPhone, normalisePhone, type Msisdn, type PhoneErrorCode } from '@wodi/domain';
import { useState } from 'react';

import { TextField, type FieldWidth } from '../TextField/TextField.tsx';

/**
 * What the person sees for each phone error. @wodi/domain's PHONE_ERRORS are developer
 * descriptions; the interface owns its own wording. Typed against PhoneErrorCode, so a
 * new code can't ship without a message.
 */
export const PHONE_ERROR_MESSAGES: Record<PhoneErrorCode, string> = {
  'phone.empty': 'Enter a phone number.',
  'phone.invalid_characters':
    'Use digits only. Spaces and dashes are fine, and a leading + for +254.',
  'phone.unsupported_country': 'Only Kenyan mobile numbers can be used. Start with 07, 01 or +254.',
  'phone.too_short':
    'This number is too short. A Kenyan mobile number has 10 digits, starting 07 or 01.',
  'phone.too_long':
    'This number is too long. A Kenyan mobile number has 10 digits, starting 07 or 01.',
  'phone.not_mobile':
    'This is not a mobile number. Enter a number starting 07 or 01 so M-Pesa and SMS reach it.',
};

export interface PhoneFieldProps {
  /** Always visible, above the field. */
  label: string;
  /** The stored form, `2547…` or `2541…`, or `null` when empty or not valid yet. */
  value: Msisdn | null;
  /**
   * Called as the person types, with the stored form when the text is a valid Kenyan
   * mobile number and `null` otherwise, plus the reason it isn't valid. The form can
   * block saving on `error` even before the field loses focus.
   */
  onChange: (value: Msisdn | null, error: PhoneErrorCode | null) => void;
  description?: string;
  /** An error from elsewhere, e.g. the server. Shown instead of the field's own. */
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  onBlur?: () => void;
  name?: string;
  /** Width by expected content. Defaults to `md`. */
  width?: FieldWidth;
}

function check(text: string, isRequired: boolean) {
  if (text.trim() === '') {
    return { value: null, error: isRequired ? ('phone.empty' as const) : null };
  }
  const result = normalisePhone(text);
  return result.ok ? { value: result.value, error: null } : { value: null, error: result.code };
}

/**
 * A Kenyan mobile number (DESIGN.md §7). Accepts any common way of typing one
 * (`0712 345 678`, `+254 712…`, `254712…`, `712…`), and on leaving the field shows it
 * as `07xx xxx xxx` or says what is wrong. The form receives only the stored form.
 */
export function PhoneField({
  label,
  value,
  onChange,
  description,
  errorMessage,
  isRequired = false,
  isDisabled,
  autoFocus,
  onBlur,
  name,
  width = 'md',
}: PhoneFieldProps) {
  const [text, setText] = useState(value === null ? '' : formatPhone(value));
  const [reported, setReported] = useState<Msisdn | null>(value);
  const [showError, setShowError] = useState(false);

  // The form changed the value itself (a restored draft, a reset): show the new number.
  if (value !== reported) {
    setReported(value);
    setText(value === null ? '' : formatPhone(value));
    setShowError(false);
  }

  const { error } = check(text, isRequired);
  const shownError =
    errorMessage ?? (showError && error !== null ? PHONE_ERROR_MESSAGES[error] : undefined);

  return (
    <TextField
      label={label}
      width={width}
      type="tel"
      inputMode="tel"
      autoComplete="off"
      value={text}
      onChange={(next) => {
        setText(next);
        const result = check(next, isRequired);
        setReported(result.value);
        onChange(result.value, result.error);
      }}
      onBlur={() => {
        const result = check(text, isRequired);
        if (result.value !== null) setText(formatPhone(result.value));
        setShowError(result.error !== null);
        onBlur?.();
      }}
      {...(description === undefined ? {} : { description })}
      {...(shownError === undefined ? {} : { errorMessage: shownError })}
      {...(isRequired ? { isRequired } : {})}
      {...(isDisabled === undefined ? {} : { isDisabled })}
      {...(autoFocus === undefined ? {} : { autoFocus })}
      {...(name === undefined ? {} : { name })}
    />
  );
}
